import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '../common/Loader/Loader';
import API from '../../utils/ApiConfig';
import { CREATE_BUDGET_ONBOARDING } from '../../utils/ApiEndpoints';
import AutomatedOnboardingFooter from '../AutomatedOnboardingFooter/AutomatedOnboardingFooter';
import ProgressDialog from '../common/ProgressDialog/ProgressDialog';
import { BUDGET_PROGRESS_DESTINATION } from '../../utils/WebSocketDestinations';
import { useUnload } from '../../utils/CustomHooks';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Card from '@material-ui/core/Card';
import { dimensionIcons } from '../Onboard/Utility';
import { ZERO_GROWTH_RATE } from '../../utils/AutoOnboardConfig';

const AutomatedOnboardingCreateBudget = props => {
  const { commonProps, versionName, onInitializationError, onDataSaveError, onNavigationError, onDataSaveNotification, onCustomError } = props;
  const {
    services: { CommonService },
  } = commonProps;

  const [pageInitialized, setPageInitialized] = useState(false); //...flag indicates if the page was loaded properly (no error)
  const [isLoading, setLoading] = useState(false);

  const { t } = useTranslation();
  const tc = (e, data) => (data ? t(`autoonboard_create_budget:${e}`, data) : t(`autoonboard_create_budget:${e}`));

  const isAutoOnboardingWithoutSampleBudgetEnabled = CommonService.isAutoOnboardingWithoutSampleBudgetEnabled;

  const [growthRates, setGrowthRates] = useState([]);
  const [selectedGrowthRate, setSelectedGrowthRate] = useState('');
  const [budgetingDimensions, setBudgetingDimensions] = useState([]);

  const [showProgress, setShowProgress] = useState(false);
  const [destroyProgress, setDestroyProgress] = useState(false);
  useUnload(() => setDestroyProgress(true));

  useEffect(() => {
    setLoading(true);
    API.get(CREATE_BUDGET_ONBOARDING, {})
      .then(response => {
        const { data } = response;

        setBudgetingDimensions(data.dimensions);
        if (!isAutoOnboardingWithoutSampleBudgetEnabled) {
          setGrowthRates(data.growthRates);
          setSelectedGrowthRate(data.selectedGrowthRate);
        }
        setPageInitialized(true);
        if (isAutoOnboardingWithoutSampleBudgetEnabled) {
          setTimeout(() => {
            focus('.pm-create-budget-button');
          }, 2000);
        }
      })
      .catch(err => {
        onInitializationError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!isAutoOnboardingWithoutSampleBudgetEnabled) {
      if (selectedGrowthRate === undefined || selectedGrowthRate === null || selectedGrowthRate === '') {
        setTimeout(() => {
          $('.MuiIconButton-label input').first().focus();
        }, 0);
      } else {
        setTimeout(() => {
          $(".MuiIconButton-label input[value='" + selectedGrowthRate + "']").focus();
        }, 0);
      }
    }
  }, [selectedGrowthRate]);

  const getCreateBudgetInputData = () => {
    let inputData = {};
    if (!isAutoOnboardingWithoutSampleBudgetEnabled) {
      inputData = { growthRate: selectedGrowthRate };
    }
    return inputData;
  };

  const handleCreateBudget = () => {
    if (!pageInitialized) {
      onCustomError(null); //...unexpected error, call of this method should not be possible
      return;
    }

    setShowProgress(true);
    API.post(CREATE_BUDGET_ONBOARDING, getCreateBudgetInputData())
      .then(() => {
        setDestroyProgress(true);
        CommonService.redirectToReportsOnboarding();
      })
      .catch(err => {
        if (isAutoOnboardingWithoutSampleBudgetEnabled) {
          onCustomError(err, tc('AUTOMATED_ONBOARDING_CREATE_PLAN_ERROR'));
        } else {
          onCustomError(err, tc('AUTOMATED_ONBOARDING_CREATE_BUDGET_ERROR'));
        }
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  // footer
  const save = (saveType, callbackfn) => {
    if (!pageInitialized) {
      onDataSaveError(null); //...unexpected error, call of this method should not be possible
      return;
    }

    setLoading(true);
    API.put(CREATE_BUDGET_ONBOARDING, getCreateBudgetInputData())
      .then(() => {
        callbackfn();
      })
      .catch(err => {
        onDataSaveError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const footerBtnInfo = {
    saveBtn: { disabled: false, showBtn: true, click: save },
    saveExitBtn: { disabled: false, showBtn: true, click: save },
    createBudgetBtn: {
      disabled: isAutoOnboardingWithoutSampleBudgetEnabled ? false : !selectedGrowthRate,
      showBtn: true,
      click: handleCreateBudget,
    },
  };

  return (
    <div className={'automated-onboarding-create-budget'}>
      <ProgressDialog show={showProgress} destroy={destroyProgress} destination={BUDGET_PROGRESS_DESTINATION} />
      {isLoading ? <Loader /> : ''}

      <div className={'automated-onboarding-create-budget-content'} style={{ minWidth: growthRates.length * 360 + 'px' }}>
        <div className="automated-onboarding-title title">
          {isAutoOnboardingWithoutSampleBudgetEnabled
            ? tc('AUTOMATED_ONBOARDING_CREATE_PLAN_TITLE')
            : tc('AUTOMATED_ONBOARDING_CREATE_BUDGET_TITLE', { versionName })}
        </div>

        {!isAutoOnboardingWithoutSampleBudgetEnabled && (
          <div className="automated-onboarding-growth-rates">
            <div className="automated-onboarding-subtitle">{tc('AUTOMATED_ONBOARDING_CREATE_BUDGET_GROWTH_RATE', { versionName })}</div>

            <RadioGroup
              aria-label="growthRate"
              name="growthRate"
              value={selectedGrowthRate}
              onChange={({ target: { value } }) => setSelectedGrowthRate(value)}
              className="automated-onboarding-growth-rate">
              <TableContainer>
                <Table style={growthRates.length === 1 ? { maxWidth: '500px', margin: 'auto' } : {}}>
                  <TableBody>
                    <TableRow>
                      {growthRates.map((item, i) => (
                        <TableCell key={i}>
                          <FormControlLabel
                            value={item.growthRateId}
                            control={
                              <Radio
                                disableRipple={true}
                                onFocus={e => {
                                  setTimeout(() => {
                                    $(e.target).next().addClass('growth-rate-focused');
                                  }, 0);
                                }}
                                onBlur={e => {
                                  $(e.target).next().removeClass('growth-rate-focused');
                                }}
                              />
                            }
                            label={
                              <Card
                                className={`automated-onboarding-growth-rate-container 
                                                                    ${
                                                                      selectedGrowthRate === item.growthRateId
                                                                        ? 'automated-onboarding-growth-rate-container-active'
                                                                        : ''
                                                                    }
                                                                `}>
                                <div className="automated-onboarding-growth-rate-icon">
                                  <i
                                    className={item.growthRateId === ZERO_GROWTH_RATE ? 'icon-Zero-Revenue-Growth' : 'fa fa-line-chart'}
                                    aria-hidden="true"
                                  />
                                </div>
                                <div className="automated-onboarding-growth-rate-title">{item.growthRate}</div>
                                <div className="automated-onboarding-growth-rate-desc">{item.description}</div>
                              </Card>
                            }
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </RadioGroup>
          </div>
        )}

        {isAutoOnboardingWithoutSampleBudgetEnabled && (
          <div className="automated-onboarding-create-plan">
            <div className="automated-onboarding-subtitle">{tc('AUTOMATED_ONBOARDING_CREATE_PLAN_INFO')}</div>

            <div className="automated-onboarding-create-plan-cta-parent">
              <div className="automated-onboarding-create-plan-cta">
                <span className="material-icons automated-onboarding-cta-tick">check_circle</span>
                {tc('AUTOMATED_ONBOARDING_CREATE_PLAN_CALL_TO_ACTION')}
              </div>
            </div>
          </div>
        )}

        <div className="automated-onboarding-dimension">
          <div className="automated-onboarding-subtitle automated-onboarding-dimension-basis">
            {isAutoOnboardingWithoutSampleBudgetEnabled
              ? tc('AUTOMATED_ONBOARDING_CREATE_PLAN_DIMENSION_BASIS')
              : tc('AUTOMATED_ONBOARDING_CREATE_BUDGET_DIMENSION_BASIS', { versionName })}
          </div>
          {budgetingDimensions && (
            <div className="automated-onboarding-dimension-list">
              {budgetingDimensions.map(item => (
                <div key={item.id} className="automated-onboarding-dimension-item">
                  <div className="automated-onboarding-dimension-icon">
                    <i className={`fa fa-${dimensionIcons[item.definitionType]}`} aria-hidden="true" />
                  </div>
                  <div className="automated-onboarding-dimension-name" title={item.name}>
                    {item.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AutomatedOnboardingFooter
        commonProps={commonProps}
        btnInfo={footerBtnInfo}
        pageInitialized={pageInitialized}
        onNavigationError={e => onNavigationError(e)}
        onDataSaveNotification={e => onDataSaveNotification(e)}
      />
    </div>
  );
};

export default AutomatedOnboardingCreateBudget;